/**
 * Core Module Integration
 *
 * Sets up the integration between main app's Apollo Client
 * and module-core's unified client system, including token refresh.
 *
 * IMPORTANT: Import and call this in _app.tsx after Apollo Client is created.
 */

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { setupCoreIntegration, storeCodeEmitter } from '@kidslabs/module-core';
import { cartSessionManager } from '@kidslabs/module-quote';
import {
    getCustomerAccessToken,
    getCustomerRefreshToken,
    setCustomerToken,
    clearAuthData,
} from '@/_application/modules/auth/model/auth-token';
import { getValidStoreCode } from '@/_application/modules/stores/model/store-view.model';
import Cookies from 'js-cookie';
import {
    MAGENTO_COOKIE_STORE_CODE,
    MAGENTO_STORE_CODE_KEY,
    X_API_KEY_HEADER_KEY,
} from '@/lib/constants';
import { logSentry } from '@/lib/sentry';
import { AuthEnum } from '@/_application/modules/auth/enum/auth';
import { WebStorage } from '@/_application/core/storage/web-storage';
import { cacheConfig } from '@/framework/client/cache.config';
// Import directly from the client-only file to avoid pulling in server-side
// visitor-session-store (which depends on ioredis → dns, a Node-only module).
import { visitorSessionClient } from '@/lib/visitor-session/visitor-session.client';

/**
 * Clear all cart-related stores across all modules (except customer).
 *
 * Called when the server returns a cart permission error such as
 * "The current user cannot perform operations on cart".
 *
 * Clears the following IndexedDB tables:
 * - cart, cartItemReference, cartItemReferenceSynchronization,
 *   cartItemReferenceSelectedLogics, draftCart, checkoutCart (module-quote)
 * - checkout (module-checkout)
 * - giftSelection (module-flash-sale)
 *
 * Does NOT clear: customer
 */
async function clearAllCartRelatedStores(errorMessage: string): Promise<void> {
    console.warn('[CoreIntegration] Cart permission error — clearing all cart stores:', errorMessage);

    try {
        // Dynamic imports — these modules are only needed on this rare error path
        const [{ clearAllQuoteStores }, { checkoutStoreAPI }] = await Promise.all([
            import('@kidslabs/module-quote'),
            import('@kidslabs/module-checkout'),
            import('@kidslabs/module-flash-sale'),
        ]);

        // module-quote: cart, cartItemReference, draftCart, checkoutCart,
        // cartItemReferenceSynchronization, cartItemReferenceSelectedLogics
        clearAllQuoteStores();

        // module-checkout: checkout store
        checkoutStoreAPI.actions.resetCheckoutStore();

        // module-flash-sale: gift selection store
        // giftSelectionStoreAPI.actions.resetAllSessions();

        console.log('[CoreIntegration] All cart-related stores cleared successfully');
    } catch (error) {
        console.error('[CoreIntegration] Error clearing cart stores:', error);
        logSentry({
            name: 'Clear Cart Stores Failed',
            transactionName: 'clearAllCartRelatedStores',
            error: error as Error,
            extra: { triggerError: errorMessage },
        });
    }
}

/**
 * Initialize core module integration
 *
 * Call this once when Apollo Client is ready (e.g., in useEffect in _app.tsx)
 *
 * @param apolloClient - The app's Apollo Client instance
 */
export function initializeCoreIntegration(
    apolloClient: ApolloClient<NormalizedCacheObject>
): void {
    // Inject js-cookie adapter into storeCodeEmitter so that cookie
    // read/write is consistent with the rest of the app (Apollo Client,
    // HTTP client, identity proxy all use js-cookie to read 'store').
    storeCodeEmitter.setCookieAdapter({
        get: (name) => Cookies.get(name) ?? null,
        set: (name, value, days) => Cookies.set(name, value, { expires: days, path: '/' }),
        remove: (name) => Cookies.remove(name, { path: '/' }),
    });

    // Inject visitorSessionClient as the CartSessionAdapter so that
    // module-quote can read/write cart IDs from Redis-backed visitor session
    // without depending on packages/app code.
    cartSessionManager.setAdapter(visitorSessionClient);

    setupCoreIntegration(apolloClient, {
        // Token getter - uses the SAME function as app's authLink
        getAccessToken: () => getCustomerAccessToken(),

        // Token setter - updates storage when token is refreshed
        setAccessToken: async (token: string) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setCustomerToken({ access_token: token } as any);
        },

        // Clear auth - called on logout or refresh failure.
        // CRITICAL: Must also clear all cart-related local stores and
        // visitor session to prevent stale cart data leaking to next session.
        clearAuth: async () => {
            // 1. Clear all cart-related Legend-State / IndexedDB stores
            await clearAllCartRelatedStores('clearAuth triggered');

            // 2. Clear visitor session cart IDs in Redis
            await visitorSessionClient.notifyLogout().catch((error: unknown) => {
                console.error('[CoreIntegration] Failed to clear visitor session on clearAuth:', error);
            });

            // 3. Clear WebStorage + NextAuth session
            await clearAuthData();
        },

        // Custom headers - match app's authLink headers
        getCustomHeaders: () => {
            const magentoStoreCode = Cookies.get(MAGENTO_COOKIE_STORE_CODE);
            const validStoreCode = getValidStoreCode(magentoStoreCode ?? '');
            const isClient = typeof window !== 'undefined';

            return {
                [MAGENTO_STORE_CODE_KEY]: validStoreCode,
                [MAGENTO_COOKIE_STORE_CODE]: validStoreCode,
                [X_API_KEY_HEADER_KEY]: isClient
                    ? (process.env.NEXT_PUBLIC_API_KEY ?? '')
                    : (process.env.API_KEY_SERVER ?? ''),
            };
        },

        // Auth error handler — token refresh failed, session is truly expired.
        // Note: clearAuth (above) already handles cart store + visitor session cleanup.
        // This callback handles logging/sentry only. Both are called together by the error link.
        onAuthError: (error: Error) => {
            console.error('[CoreIntegration] Auth error (session expired):', error.message);
            logSentry({
                name: 'Core Module Auth Error',
                transactionName: 'onAuthError',
                error,
                extra: {},
            });
        },

        // Refresh token getter - reads from NextAuth session
        getRefreshToken: () => getCustomerRefreshToken(),

        // Called when token is successfully refreshed
        onTokenRefreshed: (token) => {
            // Persist the full token (access_token to localStorage)
            if (token.access_token) {
                WebStorage.setItem(AuthEnum.ACCESS_TOKEN, token.access_token);
            }
        },

        // Called when a cart permission error is received from the server
        onCartError: (errorMessage: string) => {
            clearAllCartRelatedStores(errorMessage);
        },

        // Server-side token refresh endpoint for Identity Auth
        // Tokens are stored in Redis, so refresh must happen server-side
        serverRefreshEndpoint: '/api/v2/auth/refresh-token',

        // InMemoryCache configuration — ensures the fallback client uses
        // the same typePolicies, key fields, and merge functions as the app.
        cacheConfig,
    });

    console.log('[App] Core module integration initialized with token refresh support');
}

